import { useAlert } from 'components';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import {
  useAddToFavoriteMutation,
  useRemoveFromFavoriteMutation,
} from 'hooks/api/favorite';
import { handleMutation } from 'utils/handleMutation';
import { invalidateQueryCache } from 'hooks/useQuery';

export const useFavorites = () => {
  const alert = useAlert();
  const router = useRouter();

  const [addToFavorite, addMutation] = useAddToFavoriteMutation();
  const [removeFromFavorite, removeMutation] = useRemoveFromFavoriteMutation();

  const isLoading = addMutation.isLoading || removeMutation.isLoading;

  const refreshMe = useCallback(() => {
    invalidateQueryCache('users.me');
  }, []);

  const handleUnauthorized = useCallback(
    (e: any) => {
      if (e?.data?.message === 'No access token provided') {
        router?.replace('/login');
        return true;
      }
      return false;
    },
    [router]
  );

  const onAddSuccess = useCallback(() => {
    refreshMe();
    alert.success('Added to favorite');
  }, [alert, refreshMe]);

  const onAddError = useCallback(
    (e: any) => {
      if (handleUnauthorized(e)) {
        return;
      }
      alert.error(e?.message || 'Something went wrong');
    },
    [alert, handleUnauthorized]
  );

  const onRemoveSuccess = useCallback(() => {
    refreshMe();
    alert.success('Removed from favorite');
  }, [alert, refreshMe]);

  const onRemoveError = useCallback(
    (e: any) => {
      alert.error(e?.message || 'Something went wrong');
    },
    [alert]
  );

  handleMutation({
    ...addMutation,
    onSuccess: onAddSuccess,
    onError: onAddError,
  });

  handleMutation({
    ...removeMutation,
    onSuccess: onRemoveSuccess,
    onError: onRemoveError,
  });

  return {
    isLoading,
    addToFavorite,
    removeFromFavorite,
  };
};
